/**
 * Revolt Autopaint Cursor Glitter Trail
 * Renders a premium, high-performance canvas-based multi-color sparkle trail on mouse move.
 * Optimizes performance by sleeping the animation loop when cursor is idle.
 */
const runRevoltGlitter = () => {
  // Prevent running on server side, if already initialized, or on mobile touch devices
  if (typeof window === 'undefined' || window.RevoltGlitterInitialized) return;
  if (window.matchMedia && !window.matchMedia('(pointer: fine)').matches) return;
  window.RevoltGlitterInitialized = true;

  // Add canvas styling dynamically
  const style = document.createElement('style');
  style.textContent = `
    #revolt-glitter-canvas {
      position: fixed;
      top: 0;
      left: 0;
      width: 100vw;
      height: 100vh;
      pointer-events: none;
      z-index: 999999;
      display: block;
    }
  `;
  document.head.appendChild(style);

  // Create canvas element
  const canvas = document.createElement('canvas');
  canvas.id = 'revolt-glitter-canvas';
  document.body.appendChild(canvas);

  const ctx = canvas.getContext('2d');
  let particles = [];
  let isLoopActive = false;
  
  // Premium Automotive Paint Colors (Metallics, Candies, Pearls)
  const GLITTER_COLORS = [
    '#FF0000', // Candy Apple Red
    '#FF5E00', // Metallic Orange
    '#FFD700', // Gold Flake
    '#00DFFF', // Electric Turquoise
    '#D400FF', // Deep Magenta Pearl
    '#FFFFFF', // Silver Flake
    '#E0E0E0'  // Pearl White
  ];

  // Set canvas size
  const resizeCanvas = () => {
    canvas.width = window.innerWidth;
    canvas.height = window.innerHeight;
  };
  resizeCanvas();
  window.addEventListener('resize', resizeCanvas);

  class GlitterParticle {
    constructor(x, y) {
      this.x = x;
      this.y = y;
      
      // Randomly pick a glitter color
      this.color = GLITTER_COLORS[Math.floor(Math.random() * GLITTER_COLORS.length)];
      
      // Particle diameter (1.2px to 3.8px)
      this.size = Math.random() * 2.6 + 1.2;
      this.originalSize = this.size;
      
      // Physics (spread vectors + gravity drift)
      const angle = Math.random() * Math.PI * 2;
      const speed = Math.random() * 1.6 + 0.4;
      this.vx = Math.cos(angle) * speed;
      this.vy = Math.sin(angle) * speed + 0.25; // Subtle downward gravity pull
      
      // Lifetime (decay rate per frame)
      this.alpha = 1;
      this.decay = Math.random() * 0.025 + 0.015; // Fades out in 40-60 frames
      
      // Rotation for star shapes
      this.rotation = Math.random() * Math.PI * 2;
      this.rotationSpeed = (Math.random() - 0.5) * 0.08;
      
      // Twinkle / Shimmer parameters
      this.shimmerSpeed = Math.random() * 0.25 + 0.15;
      this.shimmerOffset = Math.random() * Math.PI;
      
      // Shape: 65% circular points, 35% four-point stars
      this.shape = Math.random() > 0.65 ? 'star' : 'dot';
    }

    update() {
      this.x += this.vx;
      this.y += this.vy;
      this.rotation += this.rotationSpeed;
      this.alpha -= this.decay;
    }

    draw() {
      if (this.alpha <= 0) return;

      ctx.save();
      ctx.translate(this.x, this.y);
      ctx.rotate(this.rotation);
      ctx.globalAlpha = this.alpha;

      // Twinkle calculation
      const shimmer = Math.sin(Date.now() * this.shimmerSpeed + this.shimmerOffset) * 0.35 + 0.65;
      const size = this.size * shimmer;

      ctx.fillStyle = this.color;
      
      // Premium metallic glow shadow
      ctx.shadowBlur = 5;
      ctx.shadowColor = this.color;

      if (this.shape === 'star') {
        // Draw a 4-point sparkle star
        ctx.beginPath();
        for (let i = 0; i < 4; i++) {
          ctx.rotate(Math.PI / 2);
          ctx.lineTo(0, size * 1.8);
          ctx.lineTo(size * 0.25, size * 0.25);
        }
        ctx.closePath();
        ctx.fill();
      } else {
        // Draw a circular dot
        ctx.beginPath();
        ctx.arc(0, 0, size, 0, Math.PI * 2);
        ctx.fill();
      }

      ctx.restore();
    }
  }

  const spawnParticles = (x, y, count) => {
    for (let i = 0; i < count; i++) {
      particles.push(new GlitterParticle(x, y));
    }
    
    // Start animation loop if it is currently sleeping
    if (!isLoopActive) {
      isLoopActive = true;
      requestAnimationFrame(tick);
    }
  };

  const tick = () => {
    // Clear canvas
    ctx.clearRect(0, 0, canvas.width, canvas.height);

    // Update and draw particles
    particles = particles.filter(p => {
      p.update();
      p.draw();
      return p.alpha > 0;
    });

    // If particles are active, keep loop running, otherwise sleep to save CPU resources
    if (particles.length > 0) {
      requestAnimationFrame(tick);
    } else {
      isLoopActive = false;
    }
  };

  // Track cursor position to determine spawn density based on move speed
  let lastX = 0;
  let lastY = 0;
  let isFirstMove = true;

  const handleCursorMove = (clientX, clientY) => {
    if (isFirstMove) {
      lastX = clientX;
      lastY = clientY;
      isFirstMove = false;
      return;
    }

    const dist = Math.hypot(clientX - lastX, clientY - lastY);
    
    // Spacing check: Only spawn particles when cursor moves > 6 pixels
    if (dist > 6) {
      // Scale particle count with movement distance (1 to 5 particles)
      const count = Math.min(Math.max(Math.floor(dist / 6), 1), 5);
      spawnParticles(clientX, clientY, count);
      
      lastX = clientX;
      lastY = clientY;
    }
  };

  // Mouse move listener
  window.addEventListener('mousemove', (e) => {
    handleCursorMove(e.clientX, e.clientY);
  });

  // Mobile touch drag listener (for responsive interaction)
  window.addEventListener('touchmove', (e) => {
    if (e.touches && e.touches.length > 0) {
      handleCursorMove(e.touches[0].clientX, e.touches[0].clientY);
    }
  }, { passive: true });
};

// Bulletproof DOM ready check to prevent document.body null errors
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', runRevoltGlitter);
} else {
  runRevoltGlitter();
}
